import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Client, FormSchema, CodeInjections } from '../types.js';
import { getClient, createClient, updateClient, getConfig, getPresetCatalog, type SenderProfileOption, type PresetCatalogItem } from '../api.js';
import SchemaEditor from '../components/SchemaEditor.js';

const DEFAULT_SCHEMA: FormSchema = {
  fields: [],
  submit_button_text: 'Submit',
  success_message: "Thanks for your submission. We'll be in touch.",
};

export default function ClientEdit() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [allowedOrigins, setAllowedOrigins] = useState('');
  const [catalog, setCatalog] = useState<PresetCatalogItem[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  // False until the client's own record loads — saving a form that never received
  // the stored config would otherwise persist an empty one.
  const [clientLoaded, setClientLoaded] = useState(false);
  const [presetState, setPresetState] = useState<Record<string, { enabled: boolean; id: string }>>({});
  const [customHead, setCustomHead] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [status, setStatus] = useState<'active' | 'paused'>('active');
  const [senderProfile, setSenderProfile] = useState<'default' | 'alternate'>('default');
  const [senderOptions, setSenderOptions] = useState<SenderProfileOption[]>([]);

  // Only used for new client creation (v2.0 creates the initial form alongside)
  const [schema, setSchema] = useState<FormSchema>(DEFAULT_SCHEMA);

  // Load the available sender profiles (their From addresses) for the dropdown.
  useEffect(() => {
    getConfig()
      .then((cfg) => setSenderOptions(cfg.sender_profiles ?? []))
      .catch(() => { /* dropdown falls back to plain labels */ });
  }, []);

  useEffect(() => {
    getPresetCatalog()
      .then((r) => { setCatalog(r.presets); setCatalogLoaded(true); })
      .catch(() => {
        // Leave catalogLoaded false — the panel shows a warning and saving will NOT
        // touch code_injections, so a transient fetch failure can't wipe the config.
        setCatalogLoaded(false);
      });
  }, []);

  useEffect(() => {
    if (isNew) return;
    getClient(id!)
      .then((res) => {
        const c: Client = res.client;
        setName(c.name);
        setAllowedOrigins(c.allowed_origins.join('\n'));
        const ci = c.code_injections;
        const ps: Record<string, { enabled: boolean; id: string }> = {};
        for (const [k, v] of Object.entries(ci?.presets ?? {})) ps[k] = { enabled: !!v.enabled, id: v.id ?? '' };
        setPresetState(ps);
        setCustomHead(ci?.custom_head ?? '');
        setCustomBody(ci?.custom_body ?? '');
        setStatus(c.status === 'paused' ? 'paused' : 'active');
        setSenderProfile(c.sender_profile === 'alternate' ? 'alternate' : 'default');
        setClientLoaded(true);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  /** Build the payload from every key we know about — catalog entries AND any key
   *  already stored on the client (so a preset retired from the catalog isn't
   *  silently dropped). Returns undefined when the catalog never loaded, so the
   *  caller omits code_injections entirely instead of saving an empty config. */
  function buildCodeInjections(): CodeInjections | undefined {
    if (!catalogLoaded) return undefined;
    if (!isNew && !clientLoaded) return undefined; // never save over config we never read
    const presets: CodeInjections['presets'] = {};
    const keys = new Set([...catalog.map((i) => i.key), ...Object.keys(presetState)]);
    for (const key of keys) {
      const st = presetState[key];
      if (!st) continue;
      presets[key] = st.id.trim()
        ? { enabled: st.enabled, id: st.id.trim() }
        : { enabled: st.enabled };
    }
    return { presets, custom_head: customHead, custom_body: customBody };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');

    // undefined when the catalog failed to load — the key is then omitted from the
    // payload so the server keeps whatever is already stored.
    const codeInjections = buildCodeInjections();

    if (isNew) {
      // New client: include form_schema for initial form creation
      const payload = {
        name: name.trim(),
        allowed_origins: allowedOrigins.split('\n').map((s) => s.trim()).filter(Boolean),
        ...(codeInjections ? { code_injections: codeInjections } : {}),
        form_schema: schema,
        // Legacy fields required by create endpoint — default values
        notify_emails: [] as string[],
        email_enabled: true,
        turnstile_enabled: false,
      };
      try {
        const res = await createClient(payload as Parameters<typeof createClient>[0]);
        navigate(`/clients/${res.client.id}`);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setSaving(false);
      }
    } else {
      // Existing client: only update the kept fields
      const payload = {
        name: name.trim(),
        allowed_origins: allowedOrigins.split('\n').map((s) => s.trim()).filter(Boolean),
        ...(codeInjections ? { code_injections: codeInjections } : {}),
        status,
        sender_profile: senderProfile,
      };
      try {
        await updateClient(id!, payload);
        navigate(`/clients/${id}`);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setSaving(false);
      }
    }
  }

  if (loading) return <p style={{ color: '#94a3b8' }}>Loading…</p>;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">{isNew ? 'New client' : `Edit: ${name}`}</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={(e) => { void handleSubmit(e); }}>
        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>General</h2>

          <div className="form-group">
            <label>Client name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          {!isNew && (
            <div className="form-group">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'paused')}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Allowed origins (one per line, include protocol)</label>
            <textarea
              value={allowedOrigins}
              onChange={(e) => setAllowedOrigins(e.target.value)}
              style={{ minHeight: '80px' }}
              placeholder="https://example.com"
            />
            <div className="form-hint">Exact match including protocol. e.g. https://example.com</div>
          </div>
        </div>

        <div className="card">
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Integrations &amp; Code</h2>
          {!catalogLoaded && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
              Couldn't load the integrations list. Your existing code settings are safe —
              saving will leave them untouched. Reload the page to edit them.
            </div>
          )}
          {catalog.map((item) => {
            const st = presetState[item.key] ?? { enabled: false, id: '' };
            const set = (next: { enabled: boolean; id: string }) =>
              setPresetState((s) => ({ ...s, [item.key]: next }));
            const status = !st.enabled ? 'Off' : (item.requiresId && !st.id.trim()) ? '⚠ Pending — add ID' : '✅ Active';
            return (
              <div className="form-group" key={item.key}>
                <label>
                  <input type="checkbox" checked={st.enabled}
                    onChange={(e) => set({ ...st, enabled: e.target.checked })}
                    style={{ width: 'auto', marginRight: '.4rem' }} />
                  {item.name} <span style={{ color: '#64748b', fontSize: '.8rem' }}>— {status}</span>
                </label>
                {st.enabled && item.requiresId && (
                  <input type="text" value={st.id}
                    onChange={(e) => set({ ...st, id: e.target.value })}
                    placeholder={item.idPlaceholder ?? item.idLabel ?? 'ID'}
                    style={{ marginTop: '.4rem' }} />
                )}
              </div>
            );
          })}
          <div className="form-group">
            <label>Custom &lt;head&gt; code</label>
            <textarea value={customHead} onChange={(e) => setCustomHead(e.target.value)} style={{ minHeight: '80px', fontFamily: 'monospace' }} />
          </div>
          <div className="form-group">
            <label>Custom &lt;body&gt; code</label>
            <textarea value={customBody} onChange={(e) => setCustomBody(e.target.value)} style={{ minHeight: '80px', fontFamily: 'monospace' }} />
            <div className="form-hint">Injected into this client's hosted pages. Saving re-publishes their sites automatically.</div>
          </div>
        </div>

        {!isNew && (
          <div className="card">
            <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Email sender</h2>
            <div className="form-group">
              <label>Send notifications from</label>
              <select
                value={senderProfile}
                onChange={(e) => setSenderProfile(e.target.value as 'default' | 'alternate')}
              >
                {(senderOptions.length > 0
                  ? senderOptions
                  : [{ value: 'default' as const, email: 'noreply@conversionhive.au' }, { value: 'alternate' as const, email: 'alternate sender' }]
                ).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.email || opt.value}
                  </option>
                ))}
              </select>
              <div className="form-hint">Which address this client's form-notification emails are sent from.</div>
            </div>
          </div>
        )}

        {isNew ? (
          <div className="card">
            <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Initial form schema</h2>
            <p style={{ color: '#64748b', fontSize: '.9rem', marginBottom: '1rem' }}>
              Set up the first form for this client. You can add more forms and edit notifications, webhooks, and security settings from the client detail page after creation.
            </p>
            <SchemaEditor schema={schema} onChange={setSchema} />
          </div>
        ) : (
          <div className="card">
            <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Forms</h2>
            <p style={{ color: '#64748b', fontSize: '.9rem' }}>
              <em>Form schema, notifications, webhooks, and Turnstile are now per-form (v2.0).
              Manage them on each form's edit page (linked from the client detail page).</em>
            </p>
          </div>
        )}

        <div style={{ display: 'flex', gap: '.75rem', marginTop: '1rem' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create client' : 'Save changes'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
        </div>
      </form>
    </>
  );
}
